import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { StatsInput, StatsInputDual } from './StatsInput'
import GenderPicker from './GenderPicker'
import UnitPicker from './UnitPicker'
import LSButton from '../LSButton'
import {
  poundsToKilograms,
  stonesAndPoundsToKilograms,
  feetAndInchesToCentimeters,
  kilogramsToPounds,
  centimetersToFeetAndInches,
  kilogramsToStonesAndPounds,
} from '../../metricsConverter'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import {
  trackEnterBasicInformation,
  trackErrorMessage,
  trackSelectGender,
} from '../../analytics'
import { pushToDataLayer } from 'js/pages/LandingPages/utils'
import { MAINTAIN_WEIGHT, GAIN_WEIGHT, LOSE_WEIGHT } from 'js/goals'
import { FEMALE, MALE } from 'js/genders'
import Box from './Box'
import useLocale from '../../hooks/useLocale'
import StepHeading from './StepHeading'
import StepCopy from './StepCopy'
import { userDataShape } from '../AccountFlow/accountFlowMachine'
import { calculateBMI } from 'js/utils'

function SignUpStats({ onChange, onBack, onComplete, userData }) {
  const cm2ft = (height) =>
    height !== null
      ? userData.heightUnit === 'ft'
        ? Object.values(centimetersToFeetAndInches(height || 0))
        : height
      : null
  const getHeightInCm = (height) =>
    height !== null
      ? userData.heightUnit === 'ft'
        ? feetAndInchesToCentimeters(height[0] || 0, height[1] || 0)
        : height
      : null
  const kg2other = (weight) =>
    weight !== null
      ? userData.weightUnit === 'lbs'
        ? kilogramsToPounds(weight)
        : userData.weightUnit === 'st'
        ? Object.values(kilogramsToStonesAndPounds(weight || 0))
        : weight
      : null
  const getWeightInKg = (weight) => {
    if (weight === null) return null
    if (userData.weightUnit === 'kg') return weight
    if (userData.weightUnit === 'lbs') return poundsToKilograms(weight)
    if (userData.weightUnit === 'st')
      return stonesAndPoundsToKilograms(weight[0] || 0, weight[1] || 0)
  }

  const { country } = useLocale()
  const { formatMessage } = useIntl()
  const onChangeTimer = useRef(false)
  const [age, setAge] = useState(userData.age)
  const [height, setHeight] = useState(cm2ft(userData.height))
  const [weight, setWeight] = useState(kg2other(userData.weight))
  const [goalWeight, setGoalWeight] = useState(kg2other(userData.goalWeight))
  const [error, setError] = useState(false)
  const dataToSave = {
    age,
    height: getHeightInCm(height),
    weight: getWeightInKg(weight),
    goalWeight: getWeightInKg(goalWeight),
  }

  useEffect(() => {
    clearTimeout(onChangeTimer.current)
    onChangeTimer.current = setTimeout(() => {
      onChange(dataToSave)
    }, 500)
    return () => {
      clearTimeout(onChangeTimer.current)
    }
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [age, height, weight, goalWeight])

  const [ageValid, setAgeValid] = useState(false)
  const [ageValidatedOnMount, setAgeValidatedOnMount] = useState(false)
  const [heightValid, setHeightValid] = useState(false)
  const [weightValid, setWeightValid] = useState(false)
  const [goalWeightValid, setGoalWeightValid] = useState(
    userData.goal === MAINTAIN_WEIGHT,
  )
  const [lastPressedKey, setLastPressedKey] = useState(null)

  const jumpToNext = () => {
    const keyIs0To9 =
      lastPressedKey &&
      lastPressedKey.length === 1 &&
      !isNaN(parseInt(lastPressedKey, 10))

    if (keyIs0To9 && document.activeElement.tagName === 'INPUT') {
      const getNextSibling = (elem, selector) => {
        let sibling = elem.nextElementSibling
        while (sibling) {
          if (sibling.matches(selector)) return sibling
          sibling = sibling.nextElementSibling
        }
      }

      const next =
        document.activeElement &&
        getNextSibling(
          document.activeElement.closest(
            '[data-jump-wrapper]:not([data-jump-wrapper-dual])',
          ),
          '[data-jump-wrapper]',
        )

      if (next) {
        const target = next.querySelector('[data-jump-target]')

        if (target.disabled) {
          setTimeout(() => target.focus(), 50)
        } else {
          target.focus()
        }
      }
    }
  }

  const checkForErrors = (
    value,
    type,
    { min, max, min2, max2 },
    specialChecks,
    shouldCheckForErrors,
    jump = true,
  ) => {
    let errorMessage
    let errorType
    const validitySetters = {
      age: setAgeValid,
      height: setHeightValid,
      weight: setWeightValid,
      goalWeight: setGoalWeightValid,
    }
    const isSingleField = typeof value === 'number'

    if (!shouldCheckForErrors) {
      return validitySetters[type](false)
    }

    const valueIsTooHighOrLow =
      (isSingleField && (value < min || value > max)) ||
      (value[0] !== null && value[0] < min) ||
      (value[0] !== null && value[0] > max) ||
      (value[1] !== null && value[1] < min2) ||
      (value[1] !== null && value[1] > max2)

    if (valueIsTooHighOrLow) errorMessage = formatMessage(messages.genericError)

    if (specialChecks) {
      specialChecks.forEach((specialCheck) => {
        if (specialCheck.criteria) {
          errorMessage = specialCheck.errorMessage
          errorType = specialCheck.errorType
        }
      })
    }

    if (errorType)
      trackErrorMessage({
        type: errorType,
        category: 'Signup',
      })

    if (errorMessage) {
      setError({ type, errorMessage })
      validitySetters[type](false)
      return
    }

    if (error.type === type) setError(false)
    validitySetters[type](true)

    // For inches/pounds (0–11|13), we can’t know if the user wants to write "1"
    // or e.g. "10", so prevent jumping to next if value is 1.
    if (jump && (!value[1] || (value[1] && value[1] !== 1))) jumpToNext()
  }

  const validateAge = (checkLength = true) => {
    checkForErrors(
      age,
      'age',
      { min: 13, max: 99 },
      [
        {
          criteria: age < 13,
          errorMessage: formatMessage(messages.youNeedToBe13),
          errorType: 'Age too low',
        },
      ],
      age === null ? false : !checkLength || age.toString().length > 1,
    )
  }

  useEffect(() => {
    validateAge(ageValidatedOnMount)
    if (!ageValidatedOnMount) setAgeValidatedOnMount(true)
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [age])

  const [heightValidatedOnMount, setHeightValidatedOnMount] = useState(false)
  const [heightUnit, setHeightUnit] = useState(userData.heightUnit || 'cm')
  const [weightUnit, setWeightUnit] = useState(userData.weightUnit || 'kg')

  const validateHeight = (checkLength = true) => {
    const ft = heightUnit === 'ft'

    checkForErrors(
      height,
      'height',
      {
        min: ft ? 4 : 120,
        max: ft ? 6 : 250,
        min2: ft && 0,
        max2: ft && 11,
      },
      [],
      height === null || height[0] === null
        ? false
        : !checkLength || height.toString().length > (ft ? 0 : 2),
    )
  }

  useEffect(() => {
    validateHeight(heightValidatedOnMount)
    if (!heightValidatedOnMount) setHeightValidatedOnMount(true)
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height])

  const [weightValidatedOnMount, setWeightValidatedOnMount] = useState(false)

  const validateWeight = (checkLength = true, changedType) => {
    const st = weightUnit === 'st'
    const lbs = weightUnit === 'lbs'
    const minMax = {
      min: lbs ? kilogramsToPounds(30) : st ? 5 : 30,
      max: lbs ? kilogramsToPounds(999) : st ? 39 : 999,
      min2: st && 0,
      max2: st && 13,
    }
    const lengthCheckValue = (value) => {
      const firstDigit = parseInt(value.toString().split('').shift(), 10)
      if (st) return firstDigit <= 6 ? 1 : 0
      if (lbs) return firstDigit <= 6 ? 2 : 1
      return firstDigit <= 2 ? 2 : 1
    }

    // Weight
    checkForErrors(
      weight,
      'weight',
      minMax,
      [
        {
          criteria:
            calculateBMI(weight, height) < 14 ||
            calculateBMI(weight, height) > 80,
          errorMessage: (
            <FormattedMessage
              id="SignUp.weightNotSupported"
              defaultMessage="Weight is not supported"
            />
          ),
          errorType: 'Weight is not supported',
        },
      ],
      weight === null || weight[0] === null
        ? false
        : !checkLength ||
            (st ? weight[0].toString().length : weight.toString().length) >
              lengthCheckValue(weight),
      changedType === 'weight',
    )

    // Goal weight
    if (userData.goal !== MAINTAIN_WEIGHT) {
      checkForErrors(
        goalWeight,
        'goalWeight',
        minMax,
        [
          {
            criteria:
              calculateBMI(goalWeight, height) <
              (userData.goal === GAIN_WEIGHT ? 14 : 18.5),
            errorMessage: (
              <FormattedMessage
                id="SignUp.goalWeightLow"
                defaultMessage="Goal weight too low. Please change your goal weight."
              />
            ),
            errorType: 'Goal weight too low. Please change your goal weight.',
          },
          {
            criteria: calculateBMI(goalWeight, height) > 80,
            errorMessage: (
              <FormattedMessage
                id="SignUp.goalWeightHigh"
                defaultMessage="Goal weight too high. Please change your goal weight."
              />
            ),
            errorType: 'Goal weight too high. Please change your goal weight.',
          },
          {
            criteria:
              userData.goal === GAIN_WEIGHT &&
              getWeightInKg(goalWeight) <= getWeightInKg(weight),
            errorMessage: formatMessage(messages.goalWeightAbove),
            errorType: 'Goal weight below current weight for gain weight',
          },
          {
            criteria:
              userData.goal === LOSE_WEIGHT &&
              getWeightInKg(goalWeight) >= getWeightInKg(weight),
            errorMessage: formatMessage(messages.goalWeightBelow),
            errorType: 'Goal weight above current weight for lose weight',
          },
        ],
        goalWeight === null || goalWeight[0] === null
          ? false
          : !checkLength ||
              (st
                ? goalWeight[0].toString().length
                : goalWeight.toString().length) > lengthCheckValue(goalWeight),
        changedType === 'goalWeight',
      )
    }
  }

  useEffect(() => {
    validateWeight(weightValidatedOnMount, 'weight')
    if (!weightValidatedOnMount) setWeightValidatedOnMount(true)
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight])

  useEffect(() => {
    validateWeight(weightValidatedOnMount, 'goalWeight')
    if (!weightValidatedOnMount) setWeightValidatedOnMount(true)
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalWeight])

  useEffect(() => {
    switch (country) {
      case 'US':
        if (!userData.heightUnit) setHeightUnit('ft')
        if (!userData.weightUnit) setWeightUnit('lbs')
        break
      case 'GB':
      case 'IE':
        if (!userData.weightUnit) setWeightUnit('st')
        break
    }
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country])

  useEffect(() => {
    onChange({ heightUnit, weightUnit })
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightUnit, weightUnit])

  const setUnits = (newUnits) => {
    const newHeightUnit = newUnits.split('/').shift()
    const newWeightUnit = newUnits.split('/').pop()

    if (newHeightUnit !== heightUnit) {
      setHeightUnit(newHeightUnit)
      setHeight(null)
      if (error.type === 'height') setError(false)
    }

    if (newWeightUnit !== weightUnit) {
      setWeightUnit(newWeightUnit)
      setWeight(null)
      setGoalWeight(null)
      if (error.type === 'weight' || error.type === 'goalWeight')
        setError(false)
    }
  }

  const [gender, setGender] = useState(userData.gender || null)
  useEffect(() => {
    onChange({ gender })
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender])

  const selectGender = (gender) => {
    trackSelectGender({ gender })
    setGender(gender === 'female' ? FEMALE : MALE)
    if (error.type === 'gender') setError(false)
  }

  const stepComplete = () => {
    trackEnterBasicInformation()

    onChange({ statsDataValid: true, ...dataToSave })
    onComplete()
  }

  const checkValidity = (e) => {
    e.preventDefault()
    if (!gender)
      return setError({
        type: 'gender',
        errorMessage: formatMessage(messages.weKnow),
      })
    if (!ageValid)
      return setError({
        type: 'age',
        errorMessage: formatMessage(messages.genericError),
      })
    if (!heightValid)
      return setError({
        type: 'height',
        errorMessage: formatMessage(messages.genericError),
      })
    if (!weightValid)
      return setError({
        type: 'weight',
        errorMessage: formatMessage(messages.genericError),
      })
    if (!goalWeightValid)
      return setError({
        type: 'goalWeight',
        errorMessage: formatMessage(messages.genericError),
      })
    pushToDataLayer({
      event: 'signup_form',
      section: 'about_you',
      label: undefined,
      success: error === undefined ? '1' : '0', //send 1 if the about you is submitted successfull and the user proceeds to the next step is successfully, 0 if any error occurs
      error_message: error, //if an error occurs when the user clicks submits and proceeds to the next step send error messages, if no error send undefined
    })
    stepComplete()
  }

  let message
  switch (userData.goal) {
    case LOSE_WEIGHT:
      message = 'accountflow_stats_intro_lose'
      break
    case MAINTAIN_WEIGHT:
      message = 'accountflow_stats_intro_maintain'
      break
    case GAIN_WEIGHT:
      message = 'accountflow_stats_intro_gain'
      break
    default:
      throw new Error('userData.goal had an unexpected value')
  }

  return (
    <div className="o-width-limiter flex w-full flex-col">
      <div>
        <StepHeading>
          <FormattedMessage {...messages.physicalDetails} />
        </StepHeading>
        <StepCopy>
          <FormattedMessage
            {...messages[message]}
            tagName="p"
            values={{
              b: function Bold(...chunks) {
                return <b className="font-bold">{chunks}</b>
              },
            }}
          />
        </StepCopy>
      </div>
      <Box onBack={onBack}>
        <form data-uid="physical_details_form" action="/">
          <div className="relative pb-32">
            <GenderPicker
              gender={gender}
              selectGender={selectGender}
              error={error && error.type === 'gender' ? error : false}
            />
            <StatsInput
              value={age}
              type="age"
              label="yourAge"
              suffix={formatMessage(messages.years)}
              icon="cake"
              onChange={(value) => setAge(value)}
              onBlur={() => validateAge(false)}
              onKeyDown={(key) => setLastPressedKey(key)}
              error={error && error.type === 'age' ? error : false}
            />
            <UnitPicker
              setUnits={setUnits}
              heightUnit={heightUnit}
              weightUnit={weightUnit}
            />
            {heightUnit === 'cm' ? (
              <StatsInput
                value={height}
                type="height"
                label="yourHeight"
                labelSuffix
                suffix={formatMessage(messages[heightUnit])}
                icon="folding-rule"
                onChange={(value) => setHeight(value)}
                onBlur={() => validateHeight(false)}
                onKeyDown={(key) => setLastPressedKey(key)}
                error={error && error.type === 'height' ? error : false}
              />
            ) : (
              <StatsInputDual
                values={height}
                type="height"
                label="yourHeight"
                subLabels={[
                  formatMessage(messages.feet),
                  formatMessage(messages.inches),
                ]}
                suffixes={['′', '″']}
                icon="folding-rule"
                onChange={(value) => setHeight(value)}
                onKeyDown={(key) => setLastPressedKey(key)}
                error={error && error.type === 'height' ? error : false}
              />
            )}
            {weightUnit !== 'st' ? (
              <StatsInput
                value={weight}
                type="weight"
                label="yourWeight"
                labelSuffix
                suffix={formatMessage(messages[weightUnit])}
                icon="scale"
                onChange={(value) => setWeight(value)}
                onBlur={() => validateWeight(false)}
                onKeyDown={(key) => setLastPressedKey(key)}
                error={error && error.type === 'weight' ? error : false}
              />
            ) : (
              <StatsInputDual
                values={weight}
                type="weight"
                label="yourWeight"
                subLabels={[
                  formatMessage(messages.stones),
                  formatMessage(messages.pounds),
                ]}
                suffixes={[
                  formatMessage(messages.st),
                  formatMessage(messages.lbs),
                ]}
                icon="scale"
                onChange={(value) => setWeight(value)}
                onKeyDown={(key) => setLastPressedKey(key)}
                error={error && error.type === 'weight' ? error : false}
              />
            )}
            {userData.goal !== MAINTAIN_WEIGHT && (
              <>
                {weightUnit !== 'st' ? (
                  <StatsInput
                    value={goalWeight}
                    type="goalWeight"
                    label="goalWeight"
                    suffix={formatMessage(messages[weightUnit])}
                    icon="trophy"
                    onChange={(value) => setGoalWeight(value)}
                    onBlur={() => validateWeight(false)}
                    onKeyDown={(key) => setLastPressedKey(key)}
                    error={error && error.type === 'goalWeight' ? error : false}
                  />
                ) : (
                  <StatsInputDual
                    values={goalWeight}
                    type="goalWeight"
                    label="goalWeight"
                    subLabels={[
                      formatMessage(messages.stones),
                      formatMessage(messages.pounds),
                    ]}
                    suffixes={[
                      formatMessage(messages.st),
                      formatMessage(messages.lbs),
                    ]}
                    icon="trophy"
                    onChange={(value) => setGoalWeight(value)}
                    onKeyDown={(key) => setLastPressedKey(key)}
                    error={error && error.type === 'goalWeight' ? error : false}
                  />
                )}
              </>
            )}
          </div>
          <div data-jump-wrapper>
            <LSButton
              data-uid="signup_flow_next_button"
              data-jump-target
              link={false}
              upperCase
              size="large"
              variant="primary"
              onClick={(e) => checkValidity(e)}
              type="submit"
              className="md:w-full"
            >
              <span>
                <FormattedMessage {...messages.next} />
              </span>
            </LSButton>
          </div>
        </form>
      </Box>
    </div>
  )
}

SignUpStats.propTypes = {
  onChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  userData: userDataShape,
}

export default SignUpStats
